import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import useAuth from './hooks/useAuth';
import useBusinessData from './hooks/useBusinessData';
import usePettyCash from './hooks/usePettyCash';
import useRoles from './hooks/useRoles';
import AuthPage from './pages/AuthPage';
import BusinessList from './pages/BusinessList';
import BusinessDashboard from './pages/BusinessDashboard';
import PettyCashPage from './pages/PettyCashPage';
import Infrastructure from './pages/Infrastructure';
import Access from './pages/Access';
import Profile from './pages/Profile';
import PrivacyPage from './pages/PrivacyPage';
import ActivityFeed from './pages/ActivityFeed';
import Alerts from './pages/Alerts';
import Settings from './pages/Settings';
import useAlerts from './hooks/useAlerts';
import { OWNER_EMAIL } from './lib/permissions';
import { FamilyOfficeProvider } from './contexts/FamilyOfficeContext';

export default function App() {
  const { user, loading, error, login, logout } = useAuth();
  const {
    businesses,
    loading: bizLoading,
    getBusiness,
    createBusiness,
    deleteBusiness,
    updateBusiness,
    updateCapex,
    updateOpex,
    updateAssumptions,
    updateClaims,
    updatePettyCash,
    updateDocuments,
    uploadDocument,
    inviteToBusiness,
    getPendingInvites,
    revokeInvite,
    revokeAccess,
  } = useBusinessData(user);

  const { rolesById } = useRoles(user);
  const { alerts, loading: alertsLoading, refresh: refreshAlerts } = useAlerts(user, businesses);

  const {
    pettyCash,
    loading: pcLoading,
    balance,
    totalTopUps,
    totalExpenses,
    addExpense,
    updateExpense,
    deleteExpense,
    addTopUp,
    updateSettings: updatePcSettings,
    savePettyCash,
  } = usePettyCash(user?.uid);

  if (loading || bizLoading || pcLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <FamilyOfficeProvider user={user} businesses={businesses}>
      <Routes>
        <Route
          path="/auth"
          element={
            user ? (
              <Navigate to="/" replace />
            ) : (
              <AuthPage login={login} error={error} />
            )
          }
        />
        <Route
          path="/"
          element={
            user ? (
              <BusinessList
                user={user}
                businesses={businesses}
                logout={logout}
                createBusiness={createBusiness}
                deleteBusiness={deleteBusiness}
                updateBusiness={updateBusiness}
                inviteToBusiness={inviteToBusiness}
                getPendingInvites={getPendingInvites}
                revokeInvite={revokeInvite}
                revokeAccess={revokeAccess}
                pettyCashBalance={balance}
                pettyCashCurrency={pettyCash?.currency}
                alerts={alerts}
                alertsLoading={alertsLoading}
              />
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />
        <Route
          path="/petty-cash"
          element={
            user ? (
              <PettyCashPage
                user={user}
                pettyCash={pettyCash}
                balance={balance}
                totalTopUps={totalTopUps}
                totalExpenses={totalExpenses}
                businesses={businesses}
                addExpense={addExpense}
                updateExpense={updateExpense}
                deleteExpense={deleteExpense}
                addTopUp={addTopUp}
                updateSettings={updatePcSettings}
                savePettyCash={savePettyCash}
              />
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />
        <Route
          path="/infrastructure"
          element={
            user && user.email === OWNER_EMAIL ? (
              <Infrastructure user={user} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/access"
          element={
            user && user.email === OWNER_EMAIL ? (
              <Access
                user={user}
                businesses={businesses}
                inviteToBusiness={inviteToBusiness}
                getPendingInvites={getPendingInvites}
                revokeInvite={revokeInvite}
                revokeAccess={revokeAccess}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route
          path="/profile"
          element={
            user ? (
              <Profile user={user} logout={logout} />
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />
        <Route
          path="/activity"
          element={
            user ? (
              <ActivityFeed user={user} businesses={businesses} />
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />
        <Route
          path="/alerts"
          element={
            user ? (
              <Alerts user={user} alerts={alerts} loading={alertsLoading} refresh={refreshAlerts} />
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />
        <Route
          path="/settings"
          element={
            user ? (
              <Settings user={user} businesses={businesses} logout={logout} />
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />
        <Route
          path="/business/:id"
          element={
            user ? (
              <BusinessDashboard
                user={user}
                rolesById={rolesById}
                getBusiness={getBusiness}
                updateBusiness={updateBusiness}
                deleteBusiness={deleteBusiness}
                updateCapex={updateCapex}
                updateOpex={updateOpex}
                updateAssumptions={updateAssumptions}
                updateClaims={updateClaims}
                updatePettyCash={updatePettyCash}
                updateDocuments={updateDocuments}
                uploadDocument={uploadDocument}
                inviteToBusiness={inviteToBusiness}
                getPendingInvites={getPendingInvites}
                revokeInvite={revokeInvite}
                revokeAccess={revokeAccess}
                pettyCashBalance={balance}
                pettyCashCurrency={pettyCash?.currency}
              />
            ) : (
              <Navigate to="/auth" replace />
            )
          }
        />
      </Routes>
      </FamilyOfficeProvider>
    </BrowserRouter>
  );
}
